import React, { useEffect, useRef, useState } from 'react';
import { Palette, Settings, Database, FileCode, Cloud, Wrench, GitBranch, Link } from 'lucide-react';
import { skills } from '../data/mock';
import { Progress } from './ui/progress';

const Skills = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [animateProgress, setAnimateProgress] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setTimeout(() => setAnimateProgress(true), 500);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Group skills by category
  const skillsByCategory = skills.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {});

  const categoryIcons = {
    Frontend: Palette,
    Backend: Settings,
    Database: Database,
    Language: FileCode,
    Cloud: Cloud,
    DevOps: Wrench,
    Tools: GitBranch,
    API: Link
  };

  return (
    <section
      id="skills"
      ref={sectionRef}
      className="py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="overflow-hidden">
            <p
              className={`text-gray-400 text-sm uppercase tracking-widest mb-4 transition-all duration-700 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
              }`}
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              Technical Expertise
            </p>
          </div>
          <div className="overflow-hidden">
            <h2
              className={`text-4xl md:text-5xl font-light text-gray-900 transition-all duration-700 delay-100 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
              }`}
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              Skills & Technologies
            </h2>
          </div>
        </div>

        {/* Skills Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {Object.entries(skillsByCategory).map(([category, categorySkills], categoryIndex) => (
            <div
              key={category}
              className={`bg-gray-50 p-8 rounded-2xl hover:shadow-lg transition-all duration-500 hover:-translate-y-1 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
              }`}
              style={{ transitionDelay: `${200 + categoryIndex * 100}ms` }}
            >
              {/* Category Header */}
              <div className="flex items-center gap-3 mb-6">
                {(() => {
                  const IconComponent = categoryIcons[category] || FileCode;
                  return <IconComponent size={24} className="text-gray-500" />;
                })()}
                <h3
                  className="text-lg font-medium text-gray-900"
                  style={{ fontFamily: 'Figtree, sans-serif' }}
                >
                  {category}
                </h3>
              </div>

              {/* Skills List */}
              <div className="space-y-4">
                {categorySkills.map((skill, skillIndex) => (
                  <div
                    key={skill.name}
                    className="group"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span
                        className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors duration-300"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {skill.name}
                      </span>
                      <span
                        className="text-xs text-gray-400"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {skill.level}%
                      </span>
                    </div>
                    <Progress
                      value={animateProgress ? skill.level : 0}
                      className="h-1.5 bg-gray-200"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Core Stack Highlight */}
        <div
          className={`mt-16 text-center transition-all duration-700 delay-700 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
          }`}
        >
          <p
            className="text-gray-500 mb-6"
            style={{ fontFamily: 'Figtree, sans-serif' }}
          >
            Core Stack
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {['MongoDB', 'Express.js', 'React.js', 'Node.js'].map((tech, index) => (
              <div
                key={tech}
                className="px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-all duration-300 hover:scale-105 cursor-default"
                style={{ fontFamily: 'JetBrains Mono, monospace' }}
              >
                {tech}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
